/**
 * What the playground knows about one session's objects.
 *
 * Two kinds of state, loaded differently on purpose. The INDEX is one light row per alias: complete, and paged
 * when a session is selected. An ENTRY holds an object's value and exists only for an alias something asked for.
 * Nothing here knows React, axios or SessionManager — the I/O is injected, which is what lets the suite drive
 * every race.
 *
 * Spec: docs/specs/2026-09-17-session-object-store.md
 */

export const MAX_CONCURRENT_DOWNLOADS = 8;
export const UNWATCHED_ENTRY_LIMIT = 64;
export const CHANGE_TICK_MS = 50;
export const LIST_MAX_PAGES = 1000;

export const IDLE_ENTRY = Object.freeze({ status: "idle", value: undefined, error: null, refreshing: false });

/**
 * What a read that began in a session which has since been left resolves with.
 *
 * Never the new session's entry: `load` is awaited by callers that then WRITE, and `uploadSessionObject` binds
 * the manager's session id at call time — so handing back the entry now under that alias would merge one
 * session's patch into another session's object and upload it there. The same alias in two sessions is two
 * different objects.
 */
export const STALE_ENTRY = Object.freeze({
  status: "error", value: undefined, error: Object.freeze({ stale: true }), refreshing: false,
});

export const entryOf = (status, value, error) => ({ status, value, error: error || null, refreshing: false });

// A finishing download hands its slot straight to the next waiter, so `running` only falls when nobody waits.
function acquire(queue) {
  if (queue.running < MAX_CONCURRENT_DOWNLOADS) {
    queue.running++;
    return Promise.resolve();
  }
  return new Promise(resolve => queue.waiting.push(resolve));
}

function release(queue) {
  const next = queue.waiting.shift();
  if (next) next(); else queue.running--;
}

export default class SessionObjectStore {
  constructor({ listPage, download, onChange }) {
    this.listPage = listPage;
    this.download = download;
    this.onChange = onChange || (() => {});

    this.generation = 0;          // bumped by reset; a response from an older one is discarded
    this.active = false;          // is there a session to ask
    this.listStatus = "idle";
    this.listRun = 0;
    this.sinceList = null;        // rows touched while a list is on its way: { upserted, deleted }
    this.index = new Map();       // alias -> { alias, updatedAt, size, type }
    this.sorted = null;
    this.entries = new Map();     // alias -> { status, value, error, refreshing }
    this.watchers = new Map();    // alias -> Set<callback>
    this.inflight = new Map();    // alias -> Promise<entry>
    this.dirty = new Set();       // in flight, and already known to be out of date
    this.queue = { running: 0, waiting: [] };
    this.tickTimer = null;
    this.disposed = false;
  }

  // -- lifecycle ---------------------------------------------------------------------------------

  /**
   * A new session, or none. Keeps the watchers and tells each its entry is gone, and fetches nothing itself:
   * they re-render, and what they still read is fetched by `touch`. An alias that belonged to the old session
   * is simply not read again, so it is never asked of the new one.
   */
  reset({ active = false } = {}) {
    this.generation++;
    this.active = !!active;
    this.index = new Map();
    this.sorted = null;
    this.entries = new Map();
    this.inflight = new Map();
    this.dirty = new Set();
    this.sinceList = null;
    // Downloads of the session being left keep their own queue; their slots are never counted against this one.
    this.queue = { running: 0, waiting: [] };
    this.listStatus = "idle";
    for (const alias of [...this.watchers.keys()]) this._notify(alias);
    this._tick();
    return this.active ? this._loadIndex() : Promise.resolve();
  }

  dispose() {
    this.disposed = true;
    if (this.tickTimer) clearTimeout(this.tickTimer);
    this.tickTimer = null;
  }

  // -- the index ---------------------------------------------------------------------------------

  /**
   * Whatever the index holds now, sorted. `listStatus` says whether that is everything. Do not mutate.
   * `rows()` returns the memoized array; `rows(prefix)` filters it into a fresh array on every call — a
   * caller must not rely on either return value's identity.
   */
  rows(prefix) {
    if (!this.sorted) {
      this.sorted = [...this.index.values()].sort((a, b) => a.alias.localeCompare(b.alias));
    }
    return prefix ? this.sorted.filter(row => row.alias.startsWith(prefix)) : this.sorted;
  }

  /** Proves presence, never absence: the index lags every mutation. Gate an action on it, never a read. */
  has(alias) {
    return this.index.has(alias);
  }

  typeOf(alias) {
    const entry = this.entries.get(alias);
    if (entry && "ready" === entry.status && entry.value && entry.value.type) return entry.value.type;
    const row = this.index.get(alias);
    return (row && row.type) || null;
  }

  async _loadIndex() {
    const generation = this.generation;
    const run = ++this.listRun;
    const superseded = () => generation !== this.generation || run !== this.listRun;
    this.listStatus = "loading";
    this.sinceList = { upserted: new Set(), deleted: new Set() };
    this._tick();

    const listed = [];
    try {
      let cursor = null;
      for (let page = 0; page < LIST_MAX_PAGES; page++) {
        const data = (await this.listPage(cursor)) || {};
        if (superseded()) return;
        listed.push(...(data.objects || []));
        cursor = data.cursor || null;
        if (!cursor) break;
      }
      if (cursor) {
        // Ran out of pages without reaching the end — an unbounded loop, not a complete index.
        console.error("Session object listing did not end after", LIST_MAX_PAGES, "pages");
        this.sinceList = null;
        this.listStatus = "error";
        this._tick();
        return;
      }
    } catch (error) {
      if (superseded()) return;
      console.error("Failed to list session objects:", error);
      this.sinceList = null;
      this.listStatus = "error";
      this._tick();
      return;
    }

    const since = this.sinceList;
    this.sinceList = null;
    const previous = this.index;
    const next = new Map();
    for (const row of listed) {
      if (since.deleted.has(row.alias)) continue;
      const known = previous.get(row.alias);
      next.set(row.alias, {
        alias: row.alias,
        updatedAt: row.updated_at || null,
        size: undefined === row.size ? null : row.size,
        type: (known && known.type) || null,
      });
    }
    // A row an event, a write or a download touched while the list was on its way is newer than the list.
    for (const alias of since.upserted) {
      if (previous.has(alias)) next.set(alias, previous.get(alias));
    }
    this.index = next;
    this.sorted = null;
    this.listStatus = "ready";
    this._tick();
  }

  _upsertRow(alias, patch) {
    const row = { ...(this.index.get(alias) || { alias, updatedAt: null, size: null, type: null }) };
    for (const key of Object.keys(patch)) {
      if (undefined !== patch[key]) row[key] = patch[key];
    }
    this.index.set(alias, row);
    this.sorted = null;
    if (this.sinceList) {
      this.sinceList.upserted.add(alias);
      this.sinceList.deleted.delete(alias);
    }
  }

  _removeRow(alias) {
    this.index.delete(alias);
    this.sorted = null;
    if (this.sinceList) {
      this.sinceList.deleted.add(alias);
      this.sinceList.upserted.delete(alias);
    }
  }

  // -- entries -----------------------------------------------------------------------------------

  /** The entry as it stands. No side effects — asking is `watch`, `touch` or `load`. */
  peek(alias) {
    return this.entries.get(alias) || IDLE_ENTRY;
  }

  watch(alias, callback) {
    if (!this.watchers.has(alias)) this.watchers.set(alias, new Set());
    this.watchers.get(alias).add(callback);
    this.touch(alias);
    return () => this._unwatch(alias, callback);
  }

  /** Fetch a watched alias that has NO entry — what a reset or a drop leaves. Never retries an error. */
  touch(alias) {
    if (this.active && this.watchers.has(alias) && !this.entries.has(alias)) this._fetch(alias);
  }

  _unwatch(alias, callback) {
    const set = this.watchers.get(alias);
    if (!set) return;
    set.delete(callback);
    if (0 === set.size) {
      this.watchers.delete(alias);
      this._recent(alias);
      this._evict();
    }
  }

  /**
   * One read, for a handler that acts. Resolves — never rejects — with whatever status results. A read on a
   * READY alias that has a download already in flight joins that download instead of answering from cache:
   * the value in flight is newer, and a read-modify-write caller must never be handed the older one.
   *
   * **The promise reflects THAT READ; the stored entry is what watchers see.** `{ fresh: true }` means "this
   * read or nothing", so a download that failed resolves its own failure even though a watched `ready` entry
   * keeps its value on screen (§1.4) — otherwise a read-modify-write would pass its `status === "ready"`
   * guard on a cached body and upload it over the server's copy. A plain `load` means "the best available"
   * and falls back to that value.
   */
  load(alias, { fresh = false } = {}) {
    if (!this.active) return Promise.resolve(this.peek(alias));
    const entry = this.entries.get(alias);
    const flying = this.inflight.has(alias);
    if (!fresh && !flying && entry && "ready" === entry.status) {
      this._recent(alias);
      return Promise.resolve(entry);
    }
    if (fresh && flying) this.dirty.add(alias);   // the one in flight may predate the ask
    // The in-flight promise is shared between callers, so the per-caller distinction is drawn here.
    const generation = this.generation;
    const reading = this._fetch(alias);
    if (fresh) return reading;
    return reading.then(read => {
      // Only within the same session: an entry under this alias in a session this read did not belong to is
      // a different object, and never an answer to it.
      if ("ready" === read.status || generation !== this.generation) return read;
      const stored = this.entries.get(alias);
      return stored && "ready" === stored.status ? stored : read;
    });
  }

  refresh(alias) {
    return this.load(alias, { fresh: true });
  }

  _fetch(alias) {
    const joined = this.inflight.get(alias);
    if (joined) return joined;

    const generation = this.generation;
    const queue = this.queue;
    const current = this.entries.get(alias);
    // A body already on screen stays there while its replacement is on the way.
    this.entries.set(alias, current ? { ...current, refreshing: true } : entryOf("loading"));
    this._tick();

    const promise = acquire(queue)
      .then(() => (generation === this.generation ? this.download(alias) : Promise.reject({ stale: true })))
      .then(
        object => ({ status: "ready", value: object && object.value, updatedAt: object && object.updated_at }),
        error => (error && 403 === error.status ? { status: "missing" } : { status: "error", error })
      )
      .then(outcome => {
        release(queue);
        return this._land(alias, generation, outcome);
      });
    this.inflight.set(alias, promise);
    return promise;
  }

  _land(alias, generation, outcome) {
    // The session this download was issued for has been left: resolve stale rather than reading the entries,
    // which now hold another session's objects. See STALE_ENTRY.
    if (generation !== this.generation) return STALE_ENTRY;
    this.inflight.delete(alias);
    // What landed may predate whatever marked it — a newer local write, in the worst case. Go again.
    if (this.dirty.delete(alias)) return this._fetch(alias);
    return this._store(alias, outcome);
  }

  /**
   * Record what landed, and return what THIS read produced.
   *
   * The two differ in exactly one state: a refresh that failed over a `ready` entry. Watchers keep the value
   * — a watched object never blanks over a transient failure — while the read that failed resolves an
   * `error`, because it is the only one that knows the server was not reached.
   */
  _store(alias, outcome) {
    const current = this.entries.get(alias);
    let read;
    if ("ready" === outcome.status) {
      read = entryOf("ready", outcome.value);
      this.entries.set(alias, read);
      this._upsertRow(alias, { updatedAt: outcome.updatedAt, type: outcome.value && outcome.value.type });
    } else if ("missing" === outcome.status) {
      read = entryOf("missing");
      this.entries.set(alias, read);
    } else {
      read = entryOf("error", undefined, outcome.error);
      // A failed REFRESH: a watched object never blanks over a transient failure.
      this.entries.set(alias,
        current && "ready" === current.status ? { ...current, refreshing: false, error: outcome.error } : read);
    }
    this._recent(alias);
    this._notify(alias);
    this._tick();
    this._evict();
    return read;
  }

  // -- changes -----------------------------------------------------------------------------------

  /**
   * Object events carry an alias and a type and never a body, which is exactly enough: a watched object is
   * refetched, an unwatched one is DROPPED — keeping a stale body to show-then-swap would put wrong content on
   * screen for a round trip — and one nobody asked for costs a row update and nothing else.
   */
  applyEvents(events) {
    for (const event of events || []) {
      const alias = event && event.session_object && event.session_object.alias;
      if (!alias) continue;
      if ("session_object_modified" === event.type) {
        this._upsertRow(alias, {
          updatedAt: event.created_at, type: event.session_object.type || undefined, size: null,
        });
        this._invalidate(alias);
        this._tick();
      } else if ("session_object_deleted" === event.type) {
        this._forget(alias);
      }
    }
  }

  applyLocalWrite(alias, value) {
    if (this.inflight.has(alias)) this.dirty.add(alias);   // what is on its way predates this write
    this.entries.delete(alias);                            // re-insert, so it counts as the most recent
    this.entries.set(alias, entryOf("ready", value));
    this._upsertRow(alias, { updatedAt: new Date().toISOString(), type: value && value.type, size: null });
    this._notify(alias);
    this._tick();
    this._evict();
  }

  applyLocalDelete(alias) {
    this._forget(alias);
  }

  /** Re-page the list into a fresh index, then mark every entry: watched ones refetch, the rest are dropped. */
  reloadIndex() {
    if (!this.active) return Promise.resolve();
    const generation = this.generation;
    return this._loadIndex().then(() => {
      if (generation !== this.generation || "ready" !== this.listStatus) return;
      for (const alias of [...this.entries.keys()]) this._invalidate(alias);
      this._tick();
    });
  }

  // The value we hold for this alias is no longer known to be current.
  _invalidate(alias) {
    if (this.inflight.has(alias)) {
      this.dirty.add(alias);
    } else if (this.watchers.has(alias)) {
      this._fetch(alias);
    } else {
      this.entries.delete(alias);
    }
  }

  _forget(alias) {
    this._removeRow(alias);
    if (this.inflight.has(alias)) this.dirty.add(alias);
    if (this.watchers.has(alias)) {
      this.entries.set(alias, entryOf("missing"));
      this._notify(alias);
    } else {
      this.entries.delete(alias);
    }
    this._tick();
  }

  // -- retention ---------------------------------------------------------------------------------

  // An unwatched entry is kept, so flipping between two cells does not refetch each time — up to a limit,
  // least recently used first. "Used" is stored, released or loaded. Watched and in-flight entries never leave.
  _recent(alias) {
    const entry = this.entries.get(alias);
    if (!entry) return;
    this.entries.delete(alias);
    this.entries.set(alias, entry);
  }

  _evict() {
    const idle = alias => !this.watchers.has(alias) && !this.inflight.has(alias);
    let unwatched = 0;
    for (const alias of this.entries.keys()) if (idle(alias)) unwatched++;
    for (const alias of [...this.entries.keys()]) {
      if (unwatched <= UNWATCHED_ENTRY_LIMIT) break;
      if (!idle(alias)) continue;
      this.entries.delete(alias);
      unwatched--;
    }
  }

  // -- telling people ----------------------------------------------------------------------------

  _notify(alias) {
    const set = this.watchers.get(alias);
    if (!set) return;
    for (const callback of [...set]) {
      try {
        callback();
      } catch (e) {
        console.error(`Error notifying a watcher of ${alias}:`, e);
      }
    }
  }

  // The broad "something about objects changed" tick: at most one per window, however much changed in it.
  _tick() {
    if (this.disposed) return;
    if (this.tickTimer) return;
    this.tickTimer = setTimeout(() => {
      this.tickTimer = null;
      this.onChange();
    }, CHANGE_TICK_MS);
  }
}
